// Orquestador público del SDK (ADR 0004 §8): `KypCapture`, un `EventTarget` con los eventos
// `step | progress | complete | rejected | error`. Modo móvil: cámara → guardián → claves →
// sesión → pasos guiados → bundle firmado → `complete`. Modo escritorio: traspaso por QR y
// seguimiento por SSE.
import { signBundle } from './bundle.js';
import { CameraGuard, matchesVirtualLabel } from './CameraGuard.js';
import { CaptureApiError, CaptureClient } from './client.js';
import type { CaptureClock } from './clock.js';
import {
  browserEnvironment,
  isLocalApiBase,
  type CaptureEnvironment,
  type EventSourceLike,
} from './environment.js';
import { FrameRing } from './FrameRing.js';
import { GuidedCapture, STEP_PERCENT, type GuidedOutcome, type ShotKind } from './GuidedCapture.js';
import { normalizeLocale, translator } from './i18n/index.js';
import type { CaptureUI, RejectReason, UIAction, ViewName } from './screens.js';
import { Telemetry } from './Telemetry.js';
import type {
  CaptureBundle,
  CaptureDevice,
  CaptureHandoffEvent,
  CaptureLocale,
  CaptureResult,
  CaptureSdk,
  CaptureStep,
  CaptureVideoKind,
} from './types.js';
import { Uploader, type UploadProgress } from './Uploader.js';
import { DomCaptureUI } from './ui/view.js';
import { VideoRecorder } from './VideoRecorder.js';
import { SDK_NAME, SDK_VERSION } from './version.js';

/** Opciones de `KypCapture` (ADR 0004 §8). */
export interface KypCaptureOptions {
  /** Origen de la API de KYP (p. ej. `https://api.knowyourparcel.com`). */
  apiBase: string;
  locale?: CaptureLocale;
  /** Elemento donde se monta la interfaz (Shadow DOM). Por defecto `document.body`. */
  container?: HTMLElement | null;
  /** Guía por voz (`speechSynthesis`), apagada por defecto. Siempre hay texto además de la voz. */
  voice?: boolean;
  /** `false` apaga la telemetría de diagnóstico (las señales de seguridad se envían siempre). */
  telemetry?: boolean;
  /** Traspaso desde escritorio: el móvil crea la sesión con este `{ id, token }`. */
  handoff?: { id: string; token: string };
  /** URL de la regla A4 imprimible. */
  rulerUrl?: string;
  /** Hooks de prueba (solo con `apiBase` local o de red privada). */
  testHooks?: boolean;
  /** `auto` (por defecto): escritorio si no hay cámara; `desktop`: siempre QR; `mobile`: siempre cámara. */
  mode?: 'auto' | 'mobile' | 'desktop';
  /** Huella del bundle ya conocida (si no, se calcula con `fetch(import.meta.url)`). */
  bundleHash?: string;
  /** Interfaz propia (por defecto el Web Component interno sobre `container`). */
  ui?: CaptureUI;
  /** Entorno inyectable (pruebas y adaptaciones). */
  environment?: Partial<CaptureEnvironment>;
}

export interface StepDetail {
  step: CaptureStep;
  view?: ViewName;
}
export interface ProgressDetail {
  step: CaptureStep;
  percent: number;
  upload?: UploadProgress;
}
/** Imagen de un disparo para mostrarla en la página (el fotograma elegido, JPEG local). */
export interface CaptureViewImage {
  /** Índice del disparo en `bundle.shots`. */
  index: number;
  kind: ShotKind;
  /** Nombre legible en el idioma de la captura. */
  label: string;
  sha256: string;
  blob: Blob;
}
export interface CompleteDetail {
  result: CaptureResult;
  /** Fotogramas elegidos de cada disparo, en el orden del bundle (vacío en modo escritorio). */
  views: CaptureViewImage[];
}
export interface RejectedDetail {
  reason: RejectReason | 'validation' | 'expired';
  result?: CaptureResult;
}
export interface ErrorDetail {
  error: unknown;
  code: string;
}

/** Mapa de eventos de `KypCapture`. */
export interface KypCaptureEventMap {
  step: CustomEvent<StepDetail>;
  progress: CustomEvent<ProgressDetail>;
  complete: CustomEvent<CompleteDetail>;
  rejected: CustomEvent<RejectedDetail>;
  error: CustomEvent<ErrorDetail>;
}

/** Hooks de prueba expuestos en `capture.__test` (solo con API local). */
export interface KypCaptureTestHooks {
  /** Se llama con el bundle firmado y sus binarios justo antes de `POST …/complete`. */
  beforeSend(
    fn: (bundle: CaptureBundle, binaries: Map<string, Blob>) => void | Promise<void>,
  ): void;
  /** Pulsa el botón principal solo cada `ms` mientras se espera al usuario. */
  autoAdvance(ms: number): void;
  /** Acorta el calendario de luz a ~1,5 s (la grabación sigue necesitando ≥ 3 trozos de 1 s). */
  fastChallenge(): void;
}

/** Duración del calendario con `fastChallenge` (ms). */
const FAST_CHALLENGE_MS = 1500;

/** Restricciones de la cámara: trasera, 1280×720, 30 fps; sin audio. */
const CAMERA_CONSTRAINTS: MediaStreamConstraints = {
  audio: false,
  video: {
    facingMode: { ideal: 'environment' },
    width: { ideal: 1280 },
    height: { ideal: 720 },
    frameRate: { ideal: 30 },
  },
};

/** Sustituye a la interfaz cuando no hay DOM ni `ui` (no debería ocurrir en un navegador). */
function requireUi(opts: KypCaptureOptions, locale: CaptureLocale): CaptureUI {
  if (opts.ui) return opts.ui;
  const container = opts.container ?? globalThis.document?.body;
  if (!container) throw new Error('kyp_capture_container_required');
  return new DomCaptureUI(container, translator(locale), {
    voice: !!opts.voice,
    locale,
    ...(opts.rulerUrl ? { rulerUrl: opts.rulerUrl } : {}),
  });
}

/**
 * Captura KYP.
 *
 * ```ts
 * const capture = new KypCapture({ apiBase, locale: 'es', container });
 * capture.addEventListener('complete', (e) => e.detail.result);
 * await capture.start(); … capture.destroy();
 * ```
 */
export class KypCapture extends EventTarget {
  readonly locale: CaptureLocale;
  /** Hooks de prueba (solo si `testHooks` y `apiBase` local). */
  __test?: KypCaptureTestHooks;

  private readonly opts: KypCaptureOptions;
  private readonly env: CaptureEnvironment;
  private readonly client: CaptureClient;
  private ui: CaptureUI | null = null;
  private stream: MediaStream | null = null;
  private ring: FrameRing | null = null;
  private readonly recorders = new Set<VideoRecorder>();
  private events: EventSourceLike | null = null;
  private uploader: Uploader | null = null;
  private destroyed = false;
  private started = false;
  private currentStep: CaptureStep = 'permission';
  private autoMs = 0;
  private fast = false;
  private beforeSendFn:
    ((bundle: CaptureBundle, binaries: Map<string, Blob>) => void | Promise<void>) | null = null;
  private sdkInfo: CaptureSdk | null = null;

  constructor(opts: KypCaptureOptions) {
    super();
    if (!opts?.apiBase) throw new TypeError('kyp_capture_api_base_required');
    this.opts = opts;
    this.locale = normalizeLocale(opts.locale);
    this.env = { ...browserEnvironment(), ...opts.environment };
    this.client = new CaptureClient({ apiBase: opts.apiBase, fetch: this.env.fetch });
    if (opts.testHooks && isLocalApiBase(opts.apiBase)) {
      this.__test = {
        beforeSend: (fn) => {
          this.beforeSendFn = fn;
        },
        autoAdvance: (ms) => {
          this.autoMs = Math.max(1, ms);
        },
        fastChallenge: () => {
          this.fast = true;
        },
      };
      const w = globalThis as { window?: { __kypCapture?: KypCapture } };
      if (w.window) w.window.__kypCapture = this;
    }
  }

  override addEventListener<K extends keyof KypCaptureEventMap>(
    type: K,
    listener: ((ev: KypCaptureEventMap[K]) => void) | null,
    options?: boolean | AddEventListenerOptions,
  ): void;
  override addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | AddEventListenerOptions,
  ): void;
  override addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject | ((ev: never) => void) | null,
    options?: boolean | AddEventListenerOptions,
  ): void {
    super.addEventListener(type, listener as EventListenerOrEventListenerObject | null, options);
  }

  /**
   * Arranca la captura. Se resuelve con el resultado del servidor, o `null` si se rechazó antes de
   * abrir sesión, hubo un error o se destruyó. En modo escritorio, con el resultado del móvil.
   */
  async start(): Promise<CaptureResult | null> {
    if (this.started) throw new Error('kyp_capture_already_started');
    this.started = true;
    try {
      this.ui = requireUi(this.opts, this.locale);
      this.ui.render({ kind: 'loading' });
      const desktop =
        this.opts.mode === 'desktop' ||
        ((this.opts.mode ?? 'auto') === 'auto' && !this.opts.handoff && !(await this.hasCamera()));
      return desktop ? await this.runHandoff() : await this.runMobile();
    } catch (error) {
      if (this.destroyed) return null;
      this.fail(error);
      return null;
    } finally {
      this.stopMedia();
    }
  }

  /** Para cámara, grabadores y SSE, y desmonta la interfaz. */
  destroy(): void {
    this.destroyed = true;
    this.stopMedia();
    this.events?.close();
    this.events = null;
    this.ui?.destroy();
    this.ui = null;
    const w = globalThis as { window?: { __kypCapture?: KypCapture } };
    if (w.window?.__kypCapture === this) delete w.window.__kypCapture;
  }

  // ── Modo móvil ──────────────────────────────────────────────────────────────────────────

  private async runMobile(): Promise<CaptureResult | null> {
    const ui = this.ui!;
    const env = this.env;
    const clock = env.clock;
    ui.render({ kind: 'permission', checking: false });
    while ((await this.next()).type !== 'primary');
    ui.render({ kind: 'permission', checking: true });

    const sdk = await this.sdk();
    const telemetry = new Telemetry(this.client, {
      sdk,
      locale: this.locale,
      enabled: this.opts.telemetry !== false,
    });
    const md = env.mediaDevices;
    if (!md || typeof md.getUserMedia !== 'function') {
      await telemetry.send(
        'unsupported_browser',
        { platform: platform() },
        { reason: 'no_media_devices' },
      );
      return this.reject('unsupported');
    }
    const config = await this.client.getConfig();

    try {
      this.stream = await md.getUserMedia(CAMERA_CONSTRAINTS);
    } catch (err) {
      const name = (err as { name?: string })?.name;
      return this.reject(
        name === 'NotFoundError' || name === 'OverconstrainedError'
          ? 'no_camera'
          : 'permission_denied',
      );
    }
    if (this.destroyed) return null;
    const track = this.stream.getVideoTracks()[0];
    if (!track) return this.reject('no_camera');
    const video = ui.preview(this.stream);
    const ring = new FrameRing(env.createFrameSource(this.stream, video, clock));
    this.ring = ring;
    ring.start();

    // Guardián: si falla, NO se abre sesión; se explica y se envía la señal.
    const guard = new CameraGuard({ virtualLabels: config.virtual_camera_labels });
    const checked = await guard.check({
      track,
      devices: await md.enumerateDevices(),
      getUserMedia: md.getUserMedia,
      sample: async () => (await ring.nextFrames(1))[0]!,
    });
    const device: CaptureDevice = {
      platform: platform(),
      camera_label_hash: checked.cameraLabelHash,
      track_settings: checked.trackSettings,
    };
    if (!checked.ok) {
      const reason = checked.reason!;
      await telemetry.send(
        reason === 'virtual_camera' ? 'virtual_camera' : 'guard_failed',
        device,
        {
          reason,
          ...(checked.metrics ?? {}),
        },
      );
      return this.reject(reason);
    }
    if (this.destroyed) return null;

    const keys = await env.generateKeys();
    let session;
    try {
      session = await this.client.createSession({
        public_key: keys.publicKeyHex(),
        key_alg: keys.alg,
        sdk,
        device,
        locale: this.locale,
        ...(this.opts.handoff ? { handoff: this.opts.handoff } : {}),
      });
    } catch (err) {
      if (err instanceof CaptureApiError && err.status >= 400 && err.status < 500) {
        if (err.code === 'virtual_camera_rejected')
          await telemetry.send('virtual_camera', device, { reason: 'server' });
        return this.reject('server');
      }
      throw err;
    }
    // La lista de la sesión puede ser más reciente que la de la configuración.
    if (matchesVirtualLabel([track.label], session.virtual_camera_labels)) {
      await telemetry.send('virtual_camera', device, { reason: 'virtual_camera' });
      return this.reject('virtual_camera');
    }
    this.client.bind(session.id, keys);
    const uploader = new Uploader(this.client, {
      onProgress: (upload) => this.emitProgress(this.currentStep, upload),
    });
    this.uploader = uploader;
    this.onStep('permission');

    const guided = new GuidedCapture({
      ui,
      ring,
      session,
      signer: keys,
      clock,
      uploader,
      next: () => this.next(),
      newRecorder: (kind) =>
        this.newRecorder(kind, session.id, session.server_nonce, keys, uploader, clock),
      onStep: (step, extra) => this.onStep(step, extra),
      detectBarcode: env.barcode(video),
      motion: await env.motion().catch(() => null),
      ...(this.fast ? { challengeTargetMs: FAST_CHALLENGE_MS } : {}),
      ...(this.opts.rulerUrl ? { rulerUrl: this.opts.rulerUrl } : {}),
      sleep: env.sleep,
    });
    const outcome = await guided.run();
    ring.stop();
    return this.submit(outcome, {
      sessionId: session.id,
      sdk,
      device,
      publicKey: keys.publicKeyHex(),
      scheduleId: session.challenge.schedule_id,
      signer: keys,
      uploader,
    });
  }

  private newRecorder(
    kind: CaptureVideoKind,
    sessionId: string,
    serverNonce: string,
    signer: VideoRecorderSigner,
    uploader: Uploader,
    clock: CaptureClock,
  ): VideoRecorder {
    const rec = new VideoRecorder(this.env.createChunkSource(this.stream!, clock), {
      sessionId,
      serverNonce,
      kind,
      signer,
      onChunk: (c) => void uploader.enqueue(c.bytes, c.meta.sha256),
    });
    this.recorders.add(rec);
    return rec;
  }

  private async submit(
    outcome: GuidedOutcome,
    ctx: {
      sessionId: string;
      sdk: CaptureSdk;
      device: CaptureDevice;
      publicKey: string;
      scheduleId: string;
      signer: VideoRecorderSigner;
      uploader: Uploader;
    },
  ): Promise<CaptureResult | null> {
    const ui = this.ui!;
    const summary = {
      kind: 'summary' as const,
      shots: outcome.shots.length,
      chunks: outcome.videos.find((v) => v.kind === 'challenge')?.chunks.length ?? 0,
    };
    ui.render({ ...summary, phase: 'uploading', upload: ctx.uploader.progress() });
    const bundle = await signBundle(
      {
        sessionId: ctx.sessionId,
        sdk: ctx.sdk,
        device: ctx.device,
        publicKey: ctx.publicKey,
        shots: outcome.shots,
        videos: outcome.videos,
        scheduleId: ctx.scheduleId,
        events: outcome.events,
        declarations: outcome.declarations,
      },
      ctx.signer,
    );
    if (this.beforeSendFn) {
      const blobs = new Map<string, Blob>();
      for (const [sha, bytes] of outcome.binaries)
        blobs.set(sha, new Blob([new Uint8Array(bytes)]));
      await this.beforeSendFn(bundle, blobs);
    }
    const result = await ctx.uploader.complete(bundle);
    if (this.destroyed) return null;
    ui.render({ ...summary, phase: 'done', upload: ctx.uploader.progress(), result });
    this.emitProgress('summary', undefined, 100);
    if (result.confidence_level === 'rejected')
      this.emit('rejected', { reason: 'validation', result } satisfies RejectedDetail);
    else
      this.emit('complete', { result, views: this.viewImages(outcome) } satisfies CompleteDetail);
    return result;
  }

  /** Fotograma elegido de cada disparo como `Blob` JPEG, con su nombre traducido. */
  private viewImages(outcome: GuidedOutcome): CaptureViewImage[] {
    const t = translator(this.locale);
    return outcome.shots.map((shot, index) => {
      const kind = outcome.shotKinds[index] ?? 'label';
      const sha256 = shot.frames[shot.chosen_frame]!.sha256;
      const bytes = outcome.binaries.get(sha256) ?? new Uint8Array(0);
      return {
        index,
        kind,
        label: kind === 'label' ? t('step.label') : t(`views.${kind}`),
        sha256,
        blob: new Blob([new Uint8Array(bytes)], { type: 'image/jpeg' }),
      };
    });
  }

  // ── Modo escritorio (traspaso por QR) ───────────────────────────────────────────────────

  private async runHandoff(): Promise<CaptureResult | null> {
    const ui = this.ui!;
    const handoff = await this.client.createHandoff({ locale: this.locale });
    let screen: Extract<Parameters<CaptureUI['render']>[0], { kind: 'handoff' }> = {
      kind: 'handoff',
      handoff,
      status: 'waiting',
    };
    ui.render(screen);
    return new Promise<CaptureResult | null>((resolve) => {
      const es = this.env.eventSource(
        this.client.handoffEventsUrl(handoff.id, handoff.watch_token),
      );
      this.events = es;
      const update = (patch: Partial<typeof screen>) => {
        screen = { ...screen, ...patch };
        ui.render(screen);
      };
      const finish = (value: CaptureResult | null) => {
        es.close();
        if (this.events === es) this.events = null;
        resolve(value);
      };
      const on = (type: CaptureHandoffEvent['type'], fn: (data: CaptureHandoffEvent) => void) =>
        es.addEventListener(type, (ev) => {
          const data = parseEvent(ev.data, type);
          if (data && !this.destroyed) fn(data);
        });
      on('opened', () => update({ status: 'opened' }));
      on('step', (d) => {
        if (!d.step) return;
        update({ status: 'opened', step: d.step });
        this.emit('step', { step: d.step } satisfies StepDetail);
      });
      on('progress', (d) => {
        const step = d.step ?? screen.step ?? 'permission';
        const percent = d.percent ?? STEP_PERCENT[step];
        update({ status: 'opened', step, percent });
        this.emit('progress', { step, percent } satisfies ProgressDetail);
      });
      on('complete', (d) => {
        if (!d.result) return;
        update({ status: 'done', result: d.result, percent: 100 });
        this.emit('complete', { result: d.result, views: [] } satisfies CompleteDetail);
        finish(d.result);
      });
      on('rejected', (d) => {
        update({ status: 'done', ...(d.result ? { result: d.result } : {}) });
        this.emit('rejected', {
          reason: 'validation',
          ...(d.result ? { result: d.result } : {}),
        } satisfies RejectedDetail);
        finish(d.result ?? null);
      });
      on('expired', () => {
        update({ status: 'expired' });
        this.emit('rejected', { reason: 'expired' } satisfies RejectedDetail);
        finish(null);
      });
    });
  }

  // ── Utilidades ──────────────────────────────────────────────────────────────────────────

  /** Próxima acción del usuario; con `autoAdvance`, el botón principal se pulsa solo. */
  private next(): Promise<UIAction> {
    const p = this.ui!.action();
    if (!this.autoMs) return p;
    return Promise.race([
      p,
      this.env.sleep(this.autoMs).then((): UIAction => ({ type: 'primary' })),
    ]);
  }

  private onStep(step: CaptureStep, extra: { view?: ViewName } = {}): void {
    this.currentStep = step;
    this.emit('step', { step, ...extra } satisfies StepDetail);
    const percent =
      step === 'views' && extra.view
        ? STEP_PERCENT.views +
          (['front', 'side', 'top', 'label_corner'].indexOf(extra.view) *
            (STEP_PERCENT.ruler - STEP_PERCENT.views)) /
            4
        : STEP_PERCENT[step];
    this.emitProgress(step, undefined, Math.round(percent));
    void this.uploader?.reportProgress({ step, percent: Math.round(percent) });
  }

  private lastPercent = 0;

  private emitProgress(step: CaptureStep, upload?: UploadProgress, percent?: number): void {
    if (percent !== undefined) this.lastPercent = percent;
    this.emit('progress', {
      step,
      percent: this.lastPercent,
      ...(upload ? { upload } : {}),
    } satisfies ProgressDetail);
  }

  private emit(type: keyof KypCaptureEventMap, detail: unknown): void {
    if (this.destroyed) return;
    this.dispatchEvent(new CustomEvent(type, { detail }));
  }

  private reject(reason: RejectReason): null {
    this.stopMedia();
    this.ui?.render({ kind: 'rejected', reason });
    this.emit('rejected', { reason } satisfies RejectedDetail);
    return null;
  }

  private fail(error: unknown): void {
    const code =
      error instanceof CaptureApiError
        ? error.code
        : error instanceof Error
          ? error.message.slice(0, 64)
          : 'unknown';
    this.ui?.render({ kind: 'error' });
    this.emit('error', { error, code } satisfies ErrorDetail);
    if (this.sdkInfo)
      void new Telemetry(this.client, {
        sdk: this.sdkInfo,
        locale: this.locale,
        enabled: this.opts.telemetry !== false,
      }).send('sdk_error', { platform: platform() }, { code });
  }

  private stopMedia(): void {
    this.ring?.stop();
    this.ring = null;
    for (const r of this.recorders) void r.stop().catch(() => undefined);
    this.recorders.clear();
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
  }

  private async hasCamera(): Promise<boolean> {
    const md = this.env.mediaDevices;
    if (!md || typeof md.enumerateDevices !== 'function') return false;
    try {
      return (await md.enumerateDevices()).some((d) => d.kind === 'videoinput');
    } catch {
      return false;
    }
  }

  private async sdk(): Promise<CaptureSdk> {
    if (this.sdkInfo) return this.sdkInfo;
    let hash = this.opts.bundleHash ?? (await this.env.bundleHash());
    if (!hash || !/^[0-9a-f]{64}$/.test(hash)) {
      console.warn('[kyp] No se pudo calcular la huella del SDK; se envía 0×64.');
      hash = '0'.repeat(64);
    }
    this.sdkInfo = { name: SDK_NAME, version: SDK_VERSION, bundle_hash: hash };
    return this.sdkInfo;
  }
}

type VideoRecorderSigner = ConstructorParameters<typeof VideoRecorder>[1]['signer'];

/** Plataforma sin datos personales: `web` + familia del sistema si se sabe. */
function platform(): string {
  const ua = (globalThis.navigator?.userAgent ?? '').toLowerCase();
  if (/android/.test(ua)) return 'web-android';
  if (/iphone|ipad|ipod/.test(ua)) return 'web-ios';
  if (/mac os|windows|linux|cros/.test(ua)) return 'web-desktop';
  return 'web';
}

function parseEvent(data: string, type: CaptureHandoffEvent['type']): CaptureHandoffEvent | null {
  try {
    const parsed = JSON.parse(data) as Partial<CaptureHandoffEvent>;
    return { at: '', ...parsed, type } as CaptureHandoffEvent;
  } catch {
    return null;
  }
}
